import LoadingOverlay from '@atoms/LoadingOverlay/LoadingOverlay';
import { IAddToCartModal } from '@models/IAddToCartModal';
import { IAddToCartTray } from '@models/IAddToCartTray';
import { ICrossSellTabbedProductsList } from '@models/ICrossSellTabbedProductsList';
import { IMessage } from '@models/IMessage';
import { IProductTrackingData } from '@models/IProductTrackingData';
import { ITabbedProductsCrossSellList } from '@models/ITabbedProductsCrossSellList';
import AddToCartErrorEnum from '@models/enums/AddToCartErrorEnum';
import InfoTypeEnum from '@models/enums/InfoTypeEnum';
import TagTypesEnum from '@models/enums/TagTypesEnum';
import AddToCartTray from '@molecules/AddToCartTray/AddToCartTray';
import InfoModal from '@molecules/InfoModal/InfoModal';
import TabbedCrossSellProductListing from '@molecules/TabbedCrossSellProductListing/TabbedCrossSellProductListing';
import useCartSizeStore from '@state/CartSizeStore';
import useProductPageContentStore from '@state/ProductPageStore';
import updateDataLayer from '@utils/helpers/gtm';
import mapToNewTrackingData from '@utils/helpers/mapToNewTrackingData';
import useMediaQuery from '@utils/hooks/useMediaQuery';
import axios, { AxiosError } from 'axios';
import classNames from 'classnames';
import Cookies from 'js-cookie';
import { useRef, useState } from 'react';

const TabbedProductsCrossSellList = ({
  addToCartErrorLabel,
  basketLink,
  changeCartItemEndpoint,
  maxBasketQuantityText,
  maxBasketValueText,
  maxProductQuantityText,
  tabs,
  ...rest
}: Readonly<ITabbedProductsCrossSellList>) => {
  const mainContentRef = useRef<HTMLDivElement>(null);
  const isMobile = useMediaQuery(1024);

  const { cartSizeName, setCartSize } = useCartSizeStore((state) => ({
    cartSizeName: state.cartSizeName,
    setCartSize: state.setCartSize,
  }));

  const { productPageContent } = useProductPageContentStore();
  const { tabbedProductsCrossSellList } = productPageContent;

  const [isLoading, setIsLoading] = useState(false);
  const [isAddToCartModalOpen, setIsAddToCartModalOpen] = useState(false);
  const [isAddToCartTrayOpen, setIsAddToCartTrayOpen] = useState(false);
  const [addToCartTrayPayload, setAddToCartTrayPayload] =
    useState<IAddToCartTray>();
  const [addToCartModalContent, setAddToCartModalContent] =
    useState<IAddToCartModal>({
      title: '',
      description: '',
      type: InfoTypeEnum.Error,
    });
  const [currentItemTrackingData, setCurrentItemTrackingData] =
    useState<IProductTrackingData>();

  const crossSellTabs = tabbedProductsCrossSellList?.tabs ?? tabs;
  const tabsWithClearedCategories = crossSellTabs.map((tab) => ({
    ...tab,
    items: tab.items.map((item) => ({
      ...item,
      category: '',
    })),
  }));
  const itemsPayload = {
    ...rest,
    tabs: tabsWithClearedCategories,
  };

  const handleAddToCartModal = (content: IAddToCartModal) => {
    setAddToCartModalContent(content);
    setIsAddToCartModalOpen(true);
  };

  const addToCartErrorHandler = (error: AxiosError) => {
    const errorContentMap: Partial<Record<AddToCartErrorEnum, IMessage>> = {
      [AddToCartErrorEnum.maxBasketQuantity]: maxBasketQuantityText,
      [AddToCartErrorEnum.maxBasketValue]: maxBasketValueText,
      [AddToCartErrorEnum.maxProductQuantity]: maxProductQuantityText,
    };
    const errorCode = error.response?.data?.errorCode as AddToCartErrorEnum;
    const content = errorCode && errorContentMap[errorCode];

    if (content) {
      handleAddToCartModal({
        title: content.title,
        description: content.text,
        ctaLink: basketLink,
        type: InfoTypeEnum.Error,
      });
    } else {
      handleAddToCartModal({
        title: addToCartErrorLabel,
        type: InfoTypeEnum.Error,
      });
    }
  };

  const handleAddCrossSellItemToCart =
    (
      onSuccessfulAddToCart: (
        tabbedProductsList: ICrossSellTabbedProductsList
      ) => void
    ) =>
    async (code: string) => {
      try {
        setIsLoading(true);

        const { data } = await axios.post<IAddToCartTray>(
          changeCartItemEndpoint,
          {
            code,
            quantity: 1,
          }
        );

        const itemTrackingData = itemsPayload.tabs
          ?.flatMap((tab) => tab.items)
          .find((item) => item.trackingData?.id === code)?.trackingData;

        setCurrentItemTrackingData(itemTrackingData);
        setIsAddToCartTrayOpen(true);
        setAddToCartTrayPayload(data);
        onSuccessfulAddToCart(data.tabbedProductsList);
        setCartSize(Number(Cookies.get(cartSizeName)) || 0);

        if (itemTrackingData) {
          updateDataLayer({
            event: 'addToCart',
            ecommerce: {
              currency: itemTrackingData.currency,
              items: [
                {
                  ...mapToNewTrackingData(itemTrackingData, {
                    hideIndex: true,
                  }),
                  quantity: data.addedItem.selectedQuantity,
                },
              ],
              value: data.totalBasketValue,
            },
          });
        }
      } catch (error) {
        // biome-ignore lint/suspicious/noConsole: Allowed during error reporting
        console.log({ error });

        addToCartErrorHandler(error as AxiosError);
      } finally {
        setIsLoading(false);
      }
    };

  return (
    <section
      className="component-padding container bg-white text-black-100 dark:bg-black-100 dark:text-white"
      ref={mainContentRef}
      data-testid="Tabbed-Products-Cross-Sell-List"
    >
      <LoadingOverlay isLoading={isLoading} />

      <TabbedCrossSellProductListing
        backgroundColour="bg-white dark:bg-black-100"
        shadowColour="from-white dark:from-black-100"
        defaultTabType={TagTypesEnum.Five}
        itemsPayload={itemsPayload}
        onAddItemToCart={handleAddCrossSellItemToCart}
        renderProductCard
        shouldRestoreTab={false}
        titleClassName={classNames('pb-2.5', isMobile ? 'h3' : 'h2')}
        useShortCardOnMobile
      />

      {addToCartTrayPayload && (
        <AddToCartTray
          addToCartErrorLabel={addToCartErrorLabel}
          isOpen={isAddToCartTrayOpen}
          mainItemTrackingData={currentItemTrackingData}
          onSetIsOpen={setIsAddToCartTrayOpen}
          {...addToCartTrayPayload}
        />
      )}

      <InfoModal
        type={addToCartModalContent.type}
        isModalOpen={isAddToCartModalOpen}
        toggleModal={setIsAddToCartModalOpen}
        title={addToCartModalContent.title}
        description={addToCartModalContent.description}
        ctaLink={addToCartModalContent.ctaLink}
        shouldLockBody={!isAddToCartTrayOpen}
        dataTestId="Tabbed-Products-Cross-Sell-List-Add-To-Cart-Modal"
      />
    </section>
  );
};

export default TabbedProductsCrossSellList;
